import { useQuery } from '@tanstack/react-query'
import { Clock, GitCommit, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { apiClient } from '../lib/api'
import { formatRelative } from '../lib/utils'
import { Badge, type Variant } from './ui/Badge'
import { Skeleton } from './ui/Empty'

type AuditAction = 'insert' | 'update' | 'delete' | 'restore'

type AuditEntry = {
  id: string
  action: AuditAction
  entityType: string
  entityId: string
  actorType: 'user' | 'agent' | 'system'
  actorId: string | null
  actorAgentType: string | null
  diff: Record<string, { from: unknown; to: unknown }> | null
  note: string | null
  occurredAt: string
}

type HistoryResult = { results: AuditEntry[]; total: number }

const ACTION_ICON: Record<AuditAction, typeof Plus> = {
  insert: Plus,
  update: GitCommit,
  delete: Trash2,
  restore: RotateCcw,
}

const ACTION_VARIANT: Record<AuditAction, Variant> = {
  insert: 'success',
  update: 'info',
  delete: 'danger',
  restore: 'warning',
}

function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return '∅'
  if (typeof v === 'string') {
    return v.length > 120 ? `${v.slice(0, 120)}…` : v
  }
  if (v instanceof Date) return v.toISOString()
  if (typeof v === 'object') {
    try {
      const s = JSON.stringify(v)
      return s.length > 120 ? `${s.slice(0, 120)}…` : s
    } catch {
      return String(v)
    }
  }
  return String(v)
}

export function EntityHistory({
  entityType,
  entityId,
}: {
  entityType: string
  entityId: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['audit', entityType, entityId],
    queryFn: () =>
      apiClient.get<HistoryResult>(
        `/audit?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}&limit=100`,
      ),
  })

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-3/4" />
      </div>
    )
  }
  if (!data || data.results.length === 0) {
    return (
      <p className="text-xs text-zinc-500">
        Nessuna modifica tracciata. Questa entità è stata creata prima dell'attivazione dell'audit
        log, oppure non è mai stata modificata.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {data.results.map((e) => {
        const Icon = ACTION_ICON[e.action]
        const diffEntries = e.diff ? Object.entries(e.diff) : []
        return (
          <div
            key={e.id}
            className="rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2 text-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Icon className="size-3.5 text-zinc-500 shrink-0" />
                <Badge variant={ACTION_VARIANT[e.action]}>{e.action}</Badge>
                <span className="text-xs text-zinc-500">
                  {e.actorType}
                  {e.actorAgentType ? ` · ${e.actorAgentType}` : ''}
                  {e.actorId ? ` · #${e.actorId.slice(0, 8)}` : ''}
                </span>
              </div>
              <span className="text-xs text-zinc-500 shrink-0">{formatRelative(e.occurredAt)}</span>
            </div>
            {e.note && <p className="text-xs text-zinc-400 italic mt-1">{e.note}</p>}
            {diffEntries.length > 0 && (
              <ul className="mt-1.5 space-y-1 text-xs">
                {diffEntries.map(([field, change]) => (
                  <li key={field} className="grid grid-cols-12 gap-2 items-baseline">
                    <span className="col-span-3 text-zinc-500 font-mono truncate">{field}</span>
                    <span className="col-span-9 text-zinc-300 truncate">
                      <span className="text-zinc-500 line-through">{fmtValue(change.from)}</span>
                      <span className="mx-1 text-zinc-600">→</span>
                      <span className="text-zinc-100">{fmtValue(change.to)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function HistorySection({
  entityType,
  entityId,
  title = 'History',
}: {
  entityType: string
  entityId: string
  title?: string
}) {
  return (
    <section>
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
        <Clock className="size-3.5" /> {title}
      </h3>
      <EntityHistory entityType={entityType} entityId={entityId} />
    </section>
  )
}
